import useTenantState from '@/states/tenant.state';
import { useNavigate, useParams } from 'react-router-dom';
import useCompanyState from '@/states/company.state';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import CompanyHeader from '@/components/company/CompanyHeader';
import VideoPlayer from '@/components/VideoPlayer';
import { CompanyInformationSkeleton } from '@/components/company/skeletons';
import { useGetCompanyData } from './company-data/useCompanyData';
import { financialReportLabel } from './company-data/company-data.schema';
import { ICompanyData } from '@/types/company-data.types';

export default function CompanyInformation() {
  const { currentTenant } = useTenantState();
  const { companyProfileSteps } = useCompanyState();
  const navigate = useNavigate();
  const { tenant_id } = useParams();

  useCompanyProfileSteps();

  const { isLoading, isNotFound } = useCompanyProfile(currentTenant?.id);
  const { data: companyData } = useGetCompanyData(currentTenant?.id);

  const videoLink = companyProfileSteps.find(
    step => step.step_name.toLowerCase() === 'company information',
  )?.video_link;

  if (isLoading) {
    return <CompanyInformationSkeleton />;
  }

  if (isNotFound) {
    return (
      <section className="w-full flex flex-col items-center justify-center py-24">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Profil perusahaan belum tersedia</h2>
          <p className="text-gray-500 mb-6">
            Anda belum mengisi informasi perusahaan. Silakan lengkapi terlebih dahulu.
          </p>
          <button
            onClick={() => {
              if (!tenant_id) return;
              navigate(`/${tenant_id}/data-perusahaan`);
            }}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Perbaiki Data Perusahaan
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <CompanyHeader
        title="Profile"
        description="Detail profil, kontak, dan informasi dasar perusahaan."
        extraAction={videoLink ? <VideoPlayer url={videoLink} title="Company Information" /> : undefined}
      />

      <div className="mx-5 border-b border-gray-200" />

      <div className="p-5 space-y-5">
        {companyData ? (
          <CompanyProfileDisplay data={companyData} />
        ) : (
          <p className="text-sm text-gray-400 italic">Data perusahaan belum tersedia.</p>
        )}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Display Components                                      */
/* ──────────────────────────────────────────────────────── */

function CompanyProfileDisplay({ data }: { data: ICompanyData }) {
  const businessName = data.business_name || '-';
  const legalName = data.business_legal_name || '-';
  const industry = data.industry || data.business_category || '';
  const legalEntity = data.legal_entity || '';

  return (
    <div className="space-y-5">
      {/* Hero Banner */}
      <div
        className="w-full rounded-2xl p-6 text-white"
        style={{ background: 'linear-gradient(115deg, #1848FD 0%, #1330a6 55%, #071753 100%)' }}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center text-sm font-bold text-white shrink-0"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            AOS
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold leading-tight">{businessName}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {legalName !== '-' && (
                <span className="text-xs px-2.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.18)' }}>
                  {legalName}
                </span>
              )}
              {industry && (
                <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold" style={{ background: '#FE8D16' }}>
                  {industry}
                </span>
              )}
              {legalEntity && (
                <span
                  className="text-xs px-2.5 py-0.5 rounded-full font-semibold"
                  style={{ background: 'rgba(255,255,255,0.18)' }}
                >
                  {legalEntity}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <DataPemilikCard data={data} />
        <DetailBisnisCard data={data} />
        <ProdukSosialCard data={data} />
        <MasalahUtamaCard data={data} />
      </div>
    </div>
  );
}

/* ── Data Pemilik ── */
function DataPemilikCard({ data }: { data: ICompanyData }) {
  const communities = (data.business_condition_position || '')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)
    .join(', ');

  const genderLabels: Record<string, string> = { pria: 'Pria', wanita: 'Wanita' };
  const gender = genderLabels[data.owner_gender ?? ''] ?? '-';

  return (
    <InfoCard title="Data Pemilik" icon="👤">
      <InfoRow label="Nama Lengkap" value={data.client_name} />
      <InfoRow label="Nama Panggilan" value={data.client_nickname} />
      <InfoRow label="WhatsApp" value={data.owner_phone} />
      <InfoRow label="Jenis Kelamin" value={gender} />
      <InfoRow label="Domisili" value={data.regency_name || data.province_name} />
      {communities && <InfoRow label="Komunitas Bisnis" value={communities} />}
    </InfoCard>
  );
}

/* ── Detail Bisnis ── */
function DetailBisnisCard({ data }: { data: ICompanyData }) {
  const ownershipDisplay =
    data.ownership_status === 'Syirkah' && data.partnership_count
      ? `Syirkah (${data.partnership_count} Mitra)`
      : data.ownership_status || '-';

  return (
    <InfoCard title="Detail Bisnis" icon="🏢">
      <InfoRow label="Badan Hukum" value={data.legal_entity} />
      <InfoRow label="Lama Usaha" value={data.business_age_years ? `${data.business_age_years} Tahun` : undefined} />
      <InfoRow
        label="Jumlah Cabang"
        value={data.branch_count !== undefined ? `${data.branch_count} Cabang` : undefined}
      />
      <InfoRow label="Status Kepemilikan" value={ownershipDisplay} />
      <InfoRow label="Jumlah Karyawan" value={data.total_employees ? `${data.total_employees} Orang` : undefined} />
      <InfoRow label="Skala Omset Tahunan" value={data.business_scale} />
      <InfoRow label="Provinsi Lokasi" value={data.business_province_name} />
      <InfoRow label="Kabupaten/Kota Lokasi" value={data.business_regency_name} />
      <InfoRow
        label="Laporan Keuangan PSAK"
        value={data.has_financial_report ? financialReportLabel(data.has_financial_report) : undefined}
      />
    </InfoCard>
  );
}

/* ── Produk & Sosial Media ── */
function ProdukSosialCard({ data }: { data: ICompanyData }) {
  const socialAccounts = (data.social_media_marketplaces || []).filter(
    s => s.social_media_marketplace && s.account_name,
  );
  const otherProducts = (data.other_product_services || []).filter(p => p.product_service_name);

  const formatPrice = (estimated_price?: string) => {
    if (!estimated_price) return '';
    const parts = estimated_price.split('-');
    const fmt = (v: string) => {
      const num = v.replace(/\D/g, '');
      if (!num) return '';
      return `Rp ${new Intl.NumberFormat('id-ID').format(Number(num))}`;
    };
    if (parts.length === 2) {
      const min = fmt(parts[0]);
      const max = fmt(parts[1]);
      if (min && max) return `${min} - ${max}`;
      return min || max;
    }
    return fmt(parts[0]);
  };

  return (
    <InfoCard title="Produk & Sosial Media" icon="🛍️">
      {data.product_services_primary && (
        <div className="py-2 border-b border-gray-100 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400 block mb-0.5">Produk Utama</span>
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            {data.product_services_primary}
          </span>
        </div>
      )}

      {socialAccounts.length > 0 && (
        <div className="py-2 border-b border-gray-100 dark:border-gray-700 space-y-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400">Sosial Media / Marketplace</span>
          {socialAccounts.map((s, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">{s.social_media_marketplace}</span>
              <span className="font-medium text-gray-800 dark:text-gray-100">{s.account_name}</span>
            </div>
          ))}
        </div>
      )}

      {otherProducts.length > 0 && (
        <div className="py-2 space-y-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400">Kategori Produk Lainnya</span>
          {otherProducts.map((p, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-300">{p.product_service_name}</span>
              {p.estimated_price && (
                <span className="font-medium text-gray-800 dark:text-gray-100 text-right ml-4">
                  {formatPrice(p.estimated_price)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {!data.product_services_primary && socialAccounts.length === 0 && otherProducts.length === 0 && (
        <p className="text-sm text-gray-400 py-2">Belum ada data produk.</p>
      )}
    </InfoCard>
  );
}

/* ── Masalah Utama Bisnis ── */
function MasalahUtamaCard({ data }: { data: ICompanyData }) {
  const problems = [data.main_problem_1, data.main_problem_2, data.main_problem_3].filter(Boolean) as string[];

  if (problems.length === 0 && data.main_problems) {
    problems.push(
      ...data.main_problems
        .split('\n')
        .map(p => p.trim())
        .filter(Boolean),
    );
  }

  return (
    <InfoCard title="Masalah Utama Bisnis" icon="⚠️">
      {problems.length > 0 ? (
        <div className="space-y-2 pt-1">
          {problems.map((problem, i) => (
            <div
              key={i}
              className="flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 text-gray-800 dark:text-amber-200"
            >
              <span className="font-semibold text-amber-600 dark:text-amber-400 shrink-0">{i + 1}.</span>
              <span>{problem}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 py-2">Belum ada masalah utama yang diisi.</p>
      )}
    </InfoCard>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Shared primitives                                       */
/* ──────────────────────────────────────────────────────── */

function InfoCard({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
      <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 mb-3">
        <span>{icon}</span>
        <span style={{ color: '#1848FD' }}>{title}</span>
      </h2>
      <div className="border-b border-gray-200 dark:border-gray-700 mb-3" />
      <div className="space-y-0">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | number | null }) {
  const display = value !== undefined && value !== null && value !== '' ? String(value) : '-';
  return (
    <div className="flex justify-between items-start py-2 border-b border-gray-100 dark:border-gray-800 last:border-0 gap-4">
      <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 text-right">{display}</span>
    </div>
  );
}
